import { normalizeSizeLabel } from "@/lib/manufacturing/groupMembers";
import type { ComposeSlotDef } from "./types";

export type SizeDimensions = { width: number; length: number };

/** Exact WxL, or length-only wildcard like Wx12 / *x20 (any width). */
export type ThresholdSpec =
  | { mode: "exact"; width: number; length: number; label: string }
  | { mode: "length_only"; length: number; label: string }
  | { mode: "width_only"; width: number; label: string };

const EXACT_RE = /^(\d+)\s*[x×]\s*(\d+)$/i;
const LENGTH_ONLY_RE = /^[wW*]\s*[x×]\s*(\d+)$/;
const WIDTH_ONLY_RE = /^(\d+)\s*[x×]\s*[lL*]$/;

export function parseSizeDimensions(sizeLabel: string): SizeDimensions | null {
  const n = normalizeSizeLabel(sizeLabel);
  if (!n) return null;
  const [w, l] = n.split("x").map(Number);
  if (!Number.isFinite(w) || !Number.isFinite(l)) return null;
  return { width: w, length: l };
}

/**
 * Parse threshold input: `10x12`, `Wx12` / `*x12` (length decides for every width),
 * or `10xL` / `10x*` (width decides for every length).
 */
export function parseThresholdSpec(raw: string): ThresholdSpec | null {
  const t = raw.trim();
  if (!t) return null;

  const lengthOnly = t.match(LENGTH_ONLY_RE);
  if (lengthOnly) {
    const length = Number(lengthOnly[1]);
    if (!Number.isFinite(length) || length <= 0) return null;
    return { mode: "length_only", length, label: `Wx${length}` };
  }

  const widthOnly = t.match(WIDTH_ONLY_RE);
  if (widthOnly) {
    const width = Number(widthOnly[1]);
    if (!Number.isFinite(width) || width <= 0) return null;
    return { mode: "width_only", width, label: `${width}xL` };
  }

  const exact = t.match(EXACT_RE);
  if (exact) {
    const width = Number(exact[1]);
    const length = Number(exact[2]);
    if (!Number.isFinite(width) || !Number.isFinite(length) || width <= 0 || length <= 0) {
      return null;
    }
    return { mode: "exact", width, length, label: `${width}x${length}` };
  }

  const normalized = normalizeSizeLabel(t);
  if (normalized) {
    const dims = parseSizeDimensions(normalized);
    if (dims) {
      return {
        mode: "exact",
        width: dims.width,
        length: dims.length,
        label: normalized,
      };
    }
  }
  return null;
}

/** Compare footprint: width then length. Returns negative if a < b. */
export function compareSizeLabels(a: string, b: string): number {
  const da = parseSizeDimensions(a);
  const db = parseSizeDimensions(b);
  if (!da || !db) return a.localeCompare(b);
  if (da.width !== db.width) return da.width - db.width;
  return da.length - db.length;
}

/**
 * True when size is at or below the threshold (inclusive).
 * `Wx12` → compare length only; `10xL` → compare width only; `10x12` → full footprint.
 */
export function isSizeAtOrBelowThreshold(sizeLabel: string, thresholdSize: string): boolean {
  const dims = parseSizeDimensions(sizeLabel);
  if (!dims) return false;
  const spec = parseThresholdSpec(thresholdSize);
  if (!spec) {
    return compareSizeLabels(sizeLabel, thresholdSize) <= 0;
  }
  if (spec.mode === "length_only") return dims.length <= spec.length;
  if (spec.mode === "width_only") return dims.width <= spec.width;
  if (dims.width !== spec.width) return dims.width < spec.width;
  return dims.length <= spec.length;
}

export function slugFillDescription(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

export type SlotFillResolution = {
  fillDescription: string;
  /** Short identity key when the fill splits (changesWithSize or non-empty desc). */
  variantKey: string | null;
};

/**
 * Resolve the AI-ready fill description (and optional variant key) for a slot
 * given a product size.
 */
export function resolveSlotFill(
  sizeLabel: string | null,
  slot: ComposeSlotDef,
): SlotFillResolution {
  const primary = slot.description.trim();
  const secondary = slot.aboveDescription.trim();

  if (slot.changesWithSize && slot.thresholdSize.trim() && sizeLabel) {
    const below = isSizeAtOrBelowThreshold(sizeLabel, slot.thresholdSize);
    const fill = below ? primary : secondary || primary;
    const variantKey =
      slugFillDescription(fill) || (below ? "below" : "above");
    return { fillDescription: fill, variantKey };
  }

  if (slot.changesWithSize && primary) {
    return {
      fillDescription: primary,
      variantKey: slugFillDescription(primary) || "below",
    };
  }

  return {
    fillDescription: primary,
    variantKey: null,
  };
}

export function describeThresholdSpec(thresholdSize: string): string {
  const spec = parseThresholdSpec(thresholdSize);
  if (!spec) return "Enter 10x12, or Wx12 (any width, length decides).";
  if (spec.mode === "length_only") {
    return `Any width · length ≤ ${spec.length} is at/below.`;
  }
  if (spec.mode === "width_only") {
    return `Width ≤ ${spec.width} · any length is at/below.`;
  }
  return `Exact footprint ≤ ${spec.label} (width then length).`;
}
